/* eslint-disable react-refresh/only-export-components */
import * as React from 'react';

import type { SavedTripRead } from '@/types';

import { SaveSheet, type SaveSheetTarget } from './SaveSheet';

interface OpenArgs {
  target: SaveSheetTarget;
  existingTrip?: SavedTripRead | null;
  /**
   * When true, pre-expand the destination stop picker. Used by Today's
   * "Add destination" CTA so the user lands directly in the picker
   * instead of having to discover the inline expander.
   */
  expandDestination?: boolean;
}

interface SaveSheetState {
  open: (args: OpenArgs) => void;
  close: () => void;
  isOpen: boolean;
}

const SaveSheetContext = React.createContext<SaveSheetState | null>(null);

/**
 * Provider that mounts a single SaveSheet at the root of the app and
 * exposes an `open(target, existingTrip?)` handle via context. Any
 * callsite (station popover, Today/Map "save", You-tab "Add stop") can
 * open the canonical sheet with a pre-filled stop.
 */
export function SaveSheetProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [target, setTarget] = React.useState<SaveSheetTarget | null>(null);
  const [existingTrip, setExistingTrip] = React.useState<SavedTripRead | null>(null);
  const [expandDestination, setExpandDestination] = React.useState(false);

  const handleOpen = React.useCallback((args: OpenArgs): void => {
    setTarget(args.target);
    setExistingTrip(args.existingTrip ?? null);
    setExpandDestination(args.expandDestination ?? false);
    setOpen(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const value = React.useMemo<SaveSheetState>(
    () => ({ open: handleOpen, close: handleClose, isOpen: open }),
    [handleOpen, handleClose, open],
  );

  return (
    <SaveSheetContext.Provider value={value}>
      {children}
      <SaveSheet
        open={open}
        onOpenChange={setOpen}
        target={target}
        existingTrip={existingTrip}
        expandDestination={expandDestination}
      />
    </SaveSheetContext.Provider>
  );
}

export function useSaveSheet(): SaveSheetState {
  const ctx = React.useContext(SaveSheetContext);
  if (!ctx) {
    throw new Error('useSaveSheet must be used within a SaveSheetProvider');
  }
  return ctx;
}
